import { UnstyledButton } from '@mantine/core';
import { Icon } from '@uag/react-core';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import { useGetHistoryDisabledState } from 'app/modules/wallforming/components/placement-history/hooks/useGetHistoryDisabledState';
import { HistoryDialog } from 'app/modules/wallforming/components/placement-history/types/historyDialog';
import { toDisplayValue } from 'app/modules/wallforming/components/scenes/objects/articles/utils/transformationUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { HistoryTransformDetail } from 'app/modules/wallforming/components/placement-history/types/historyTransformDetail';

import 'app/modules/wallforming/custom-styles/customHistoryStyle.css';

const axes = ['X', 'Y', 'Z'];

export const HistoryPanelList = () => {
    const { t, i18n } = useTranslation();
    const disabled = useGetHistoryDisabledState();

    const history = usePlacementStore(
        useShallow((state) => ({
            dialog: state.historyDialog,
            entries: state.undoHistory,
            repeats: state.repeatHistory,
            position: state.historyPosition,
            baseId: state.historyBaseId,
            settings: state.historySettings,
            setSettings: state.setHistorySettings,
            scrollTo: state.scrollToHistory,
            repeat: state.repeatHistoryItem,
            unit: state.lengthUnit
        }))
    );

    const currentRowRef = useRef<HTMLButtonElement | null>(null);

    useLayoutEffect(() => {
        currentRowRef.current?.scrollIntoView({ block: 'nearest' });
    }, [history.dialog, history.position]);

    const entries = useMemo(
        () =>
            history.dialog === HistoryDialog.REPEAT
                ? [...history.repeats].reverse()
                : [{ id: history.baseId, label: 'historyOldestState' as const }, ...history.entries],
        [history.baseId, history.dialog, history.entries, history.repeats]
    );

    const currentIndex = useMemo(
        () => history.entries.findIndex(({ id }) => id === history.position) + 1,
        [history.entries, history.position]
    );

    const numberFormat = useMemo(
        () => new Intl.NumberFormat(i18n.resolvedLanguage, { maximumFractionDigits: 3 }),
        [i18n.resolvedLanguage]
    );

    const formatDetail = useCallback(
        (detail: HistoryTransformDetail) => {
            const formatLength = (meters: number) =>
                `${numberFormat.format(toDisplayValue(meters, history.unit))} ${history.unit}`;

            if (detail.kind === 'translation') {
                const components = detail.vector.flatMap((value, index) =>
                    Math.abs(value) < 1e-9
                        ? []
                        : [`${formatLength(Math.abs(value))} (${value < 0 ? '-' : '+'}${axes[index]})`]
                );
                return t('historyTranslationDetail', { value: components.join(', ') });
            }

            if (detail.kind === 'rotation') {
                const cardinalAxis = detail.axis.findIndex((value) => Math.abs(Math.abs(value) - 1) < 1e-6);
                const axis =
                    cardinalAxis >= 0
                        ? `${detail.axis[cardinalAxis] < 0 ? '-' : '+'}${axes[cardinalAxis]}`
                        : detail.axis.map((value, index) => `${axes[index]} ${numberFormat.format(value)}`).join(', ');
                return t('historyRotationDetail', { value: numberFormat.format(detail.degrees), axis });
            }

            return t('historyScalingDetail', { value: formatLength(detail.dimensionMeters), axis: axes[detail.axis] });
        },
        [history.unit, numberFormat, t]
    );

    return (
        <div className="history-panel-body">
            {history.dialog === HistoryDialog.SETTINGS ? (
                <>
                    <div className="history-panel-section-title">{t('undoHistory')}</div>
                    <div className="history-panel-settings">
                        {(
                            [
                                ['undoSteps', 'historyUndoSteps', 0, 1000],
                                ['memoryLimitMb', 'historyMemoryLimit', 1, 1024]
                            ] as const
                        ).map(([key, label, min, max]) => (
                            <div key={key} className="history-panel-field">
                                <label className="history-panel-field-label" htmlFor={`history-${key}`}>
                                    {t(label)}
                                </label>
                                <input
                                    className="h-7 rounded-sm border border-border-light px-2 text-right"
                                    id={`history-${key}`}
                                    max={max}
                                    min={min}
                                    step={1}
                                    type="number"
                                    value={history.settings[key]}
                                    onChange={(event) => {
                                        const value = event.target.valueAsNumber;
                                        if (Number.isFinite(value)) {
                                            history.setSettings({ ...history.settings, [key]: value });
                                        }
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                    <div className="history-panel-section-title">{t('repeatHistory')}</div>
                    <div className="history-panel-settings">
                        <div className="history-panel-field">
                            <label className="history-panel-field-label" htmlFor="history-repeatSteps">
                                {t('historyRepeatSteps')}
                            </label>
                            <input
                                className="h-7 rounded-sm border border-border-light px-2 text-right"
                                id="history-repeatSteps"
                                max={1000}
                                min={0}
                                step={1}
                                type="number"
                                value={history.settings.repeatSteps}
                                onChange={(event) => {
                                    const value = event.target.valueAsNumber;
                                    if (Number.isFinite(value)) {
                                        history.setSettings({ ...history.settings, repeatSteps: value });
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <p className="history-panel-help">{t('historySettingsHelp')}</p>
                </>
            ) : (
                <>
                    <p className="history-panel-help">
                        {t(history.dialog === HistoryDialog.REPEAT ? 'historyRepeatHelp' : 'historyUndoHelp')}
                    </p>
                    {(history.dialog === HistoryDialog.REPEAT ? history.repeats : history.entries).length === 0 && (
                        <div className="history-panel-empty">
                            <Icon aria-hidden="true" icon="history" />
                            {t('historyEmpty')}
                        </div>
                    )}
                    <div className="history-panel-scroll">
                        <div className="history-panel-list">
                            {entries.map((entry, index) => (
                                <UnstyledButton
                                    key={entry.id}
                                    ref={
                                        history.dialog === HistoryDialog.UNDO && entry.id === history.position
                                            ? currentRowRef
                                            : undefined
                                    }
                                    aria-current={
                                        history.dialog === HistoryDialog.UNDO && entry.id === history.position
                                            ? 'step'
                                            : undefined
                                    }
                                    className="history-panel-row"
                                    data-future={
                                        (history.dialog === HistoryDialog.UNDO && index > currentIndex) || undefined
                                    }
                                    disabled={disabled}
                                    onClick={() => {
                                        if (history.dialog === HistoryDialog.REPEAT) {
                                            history.repeat(entry.id);
                                        } else {
                                            history.scrollTo(entry.id);
                                        }
                                    }}
                                >
                                    <span className="history-panel-row-number">{index}.</span>
                                    <span className="history-panel-row-label">
                                        <span>{t(entry.label)}</span>
                                        {entry.details?.map((detail, detailIndex) => (
                                            <span key={detailIndex} className="history-panel-row-detail">
                                                {formatDetail(detail)}
                                            </span>
                                        ))}
                                    </span>
                                    {history.dialog === HistoryDialog.UNDO && entry.id === history.position && (
                                        <span className="history-panel-current">{t('historyCurrentState')}</span>
                                    )}
                                    <Icon
                                        aria-hidden="true"
                                        className="history-panel-row-action"
                                        icon={
                                            history.dialog === HistoryDialog.REPEAT
                                                ? 'repeat'
                                                : entry.id === history.position
                                                  ? 'check'
                                                  : 'arrow_forward'
                                        }
                                        size="sm"
                                    />
                                </UnstyledButton>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
